import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Check, Download } from "lucide-react";
import YouMightLike from "@/components/crafts/YouMightLike";
import BundleBanner from "@/components/crafts/BundleBanner";
import CraftVideo from "@/components/crafts/CraftVideo";
import api, { mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCraftsCart } from "@/context/CraftsCartContext";
import AdornmentCode from "@/pages/crafts/AdornmentCode";

const CraftPage = () => {
  const { slug } = useParams();
  const { format, isConverted } = useCurrency();
  const { add, has, setOpen } = useCraftsCart();
  const [craft, setCraft] = useState(undefined); // undefined=loading, null=not found
  const [active, setActive] = useState(0);

  useEffect(() => {
    setCraft(undefined);
    api.get(`/crafts/${slug}`).then((r) => { setCraft(r.data); document.title = `${r.data.title} · Crafts`; })
      .catch(() => setCraft(null));
  }, [slug]);

  if (craft === undefined) return <div className="max-w-6xl mx-auto px-6 py-24 text-cream/40 text-sm">Loading…</div>;
  if (craft === null) return (
    <div data-testid="craft-not-found" className="max-w-3xl mx-auto px-6 py-32 text-center">
      <h1 className="font-serif text-3xl text-cream">Not found</h1>
      <p className="text-cream/50 mt-3">This piece isn't available.</p>
      <Link to="/crafts" className="inline-block mt-8 border border-gold text-gold px-6 py-3 text-[0.66rem] tracking-[0.2em] uppercase hover:bg-gold hover:text-cocoa transition-colors">Back to Crafts</Link>
    </div>
  );

  if (craft.template === "adornment") return <AdornmentCode craft={craft} />;

  const gallery = [craft.cover_image, ...(craft.gallery || [])].filter(Boolean);
  const hero = gallery[active] || craft.cover_image;
  const inBag = has(craft.id);

  return (
    <div data-testid="craft-page" className="max-w-6xl mx-auto px-6 py-12 lg:py-16">
      <Link to="/crafts" className="text-[0.62rem] tracking-[0.2em] uppercase text-cream/40 hover:text-gold transition-colors">← Crafts</Link>
      <div className="mt-6"><BundleBanner /></div>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}>
          <div className="aspect-[4/5] bg-espresso/40 overflow-hidden">
            {hero ? <img src={mediaUrl(hero)} alt={craft.title} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-cream/20">No image</div>}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`craft-thumb-${i}`}
                  className={`w-16 h-20 overflow-hidden border ${active === i ? "border-gold" : "border-cream/15"}`}>
                  <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <div>
          {craft.category && <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold mb-3">{craft.category}</p>}
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">{craft.title}</h1>
          {craft.subtitle && <p className="text-cream/60 mt-3 text-base">{craft.subtitle}</p>}
          {craft.audience && (
            <p data-testid="craft-audience" className="mt-4 inline-block text-[0.7rem] tracking-[0.14em] uppercase text-gold/90 border border-gold/25 rounded-full px-4 py-1.5">
              {craft.audience}
            </p>
          )}
          {craft.video && (
            <CraftVideo
              src={craft.video}
              poster={hero}
              eyebrow={craft.video_eyebrow}
              heading={craft.video_heading}
              finaleAt={craft.video_finale_at}
              finaleCaption={craft.video_finale_caption}
              finaleSub={craft.video_finale_sub}
            />
          )}
          {craft.card_video && (
            <CraftVideo
              src={craft.card_video}
              poster={hero}
              eyebrow={craft.card_video_eyebrow}
              heading={craft.card_video_heading}
            />
          )}
          <p data-testid="craft-price" className="mt-6 text-2xl font-serif text-cream tabular-nums">{format(craft.price)}</p>
          {isConverted && <p className="text-[0.62rem] tracking-[0.14em] uppercase text-cream/40 mt-1">Charged in GBP · approx. conversion</p>}

          {craft.description && (
            <div className="mt-8 text-cream/75 text-[0.95rem] leading-relaxed whitespace-pre-line">{craft.description}</div>
          )}

          {(craft.learn || []).length > 0 && (
            <div data-testid="craft-learn" className="mt-8">
              <p className="text-[0.62rem] tracking-[0.24em] uppercase text-gold mb-3">What you'll learn</p>
              <ul className="space-y-2.5">
                {craft.learn.map((l, i) => (
                  <li key={i} data-testid={`craft-learn-${i}`} className="flex items-start gap-2.5 text-sm text-cream/80">
                    <Check size={15} className="text-gold mt-0.5 shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(craft.deliverables || []).length > 0 && (
            <div className="mt-8 border border-cream/12 p-5">
              <p className="text-[0.62rem] tracking-[0.24em] uppercase text-gold mb-3">What you'll receive</p>
              <ul className="space-y-2">
                {craft.deliverables.map((d, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-cream/80">
                    <Check size={15} className="text-gold shrink-0" /> {d.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 max-w-sm space-y-3">
            <button onClick={() => (inBag ? setOpen(true) : add(craft))} data-testid="craft-add-bag"
              className="w-full flex items-center justify-center gap-2 bg-gold text-cocoa px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors">
              {inBag ? <><Check size={15} /> In your bag — view</> : <><ShoppingBag size={15} /> Add to bag</>}
            </button>
            <button onClick={() => { add(craft); }} data-testid="craft-buy-now"
              className="w-full flex items-center justify-center gap-2 border border-cream/25 text-cream px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">
              <Download size={15} /> Buy now
            </button>
            <p className="text-[0.62rem] text-cream/40 leading-relaxed">
              Add as many pieces as you like, then check out together. Download links appear instantly after payment and are emailed to you.
            </p>
          </div>
        </div>
      </div>

      {(craft.before_after || []).length > 0 && (
        <section data-testid="craft-before-after" className="mt-20 border-t border-cream/12 pt-14">
          <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold text-center">See the difference</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-center mt-2 mb-10">From a plain prompt to polished couture</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {craft.before_after.map((pair, i) => (
              <div key={i} data-testid={`before-after-${i}`} className="grid grid-cols-2 gap-2">
                <figure className="relative">
                  <div className="aspect-[3/4] bg-espresso/40 overflow-hidden">
                    <img src={mediaUrl(pair.before)} alt="Plain prompt result" className="w-full h-full object-cover grayscale-[0.15]" />
                  </div>
                  <figcaption className="absolute top-2 left-2 bg-cocoa/85 text-cream/80 text-[0.55rem] tracking-[0.2em] uppercase px-2 py-1">Before</figcaption>
                </figure>
                <figure className="relative">
                  <div className="aspect-[3/4] bg-espresso/40 overflow-hidden">
                    <img src={mediaUrl(pair.after)} alt="Polished couture render" className="w-full h-full object-cover" />
                  </div>
                  <figcaption className="absolute top-2 left-2 bg-gold text-cocoa text-[0.55rem] tracking-[0.2em] uppercase px-2 py-1">After</figcaption>
                </figure>
              </div>
            ))}
          </div>
          <p className="text-center text-[0.62rem] tracking-[0.14em] uppercase text-cream/40 mt-8">The exact system inside The Vision Engine</p>
        </section>
      )}
      <YouMightLike currentSlug={craft.slug} />
    </div>
  );
};

export default CraftPage;
